<template>
  <SectionTabHoriz02Resource01
    v-if="contextData"
    :title="contextData.title"
    :description="contextData.description"
    :tabs="contextTabs"
    :theme="theme"
  />
</template>

<script setup lang="ts">
  import type { ISuiteProps } from '@/components/Shared/Suite/type.ts';

  interface IItem {
    name: string;
    description: string;
    link: string;
    thumbnail: string;
    wistiaCode?: string;
    trackingCode?: string;
  }

  interface IResourcesTabsData {
    title: string;
    description?: string;
    tabs: {
      tabName: string;
      items: IItem[];
    }[];
  }

  interface IResourcesTabs {
    component: 'resources-tabs';
    meta: {
      navigable?: string;
    };
    data: IResourcesTabsData;
  }

  type IProps = ISuiteProps<IResourcesTabs>;

  const props = defineProps<IProps>();

  const solutionsStore = useSolutionsStore();
  const { themeStyle } = storeToRefs(solutionsStore);

  const { provideFormatUrl } = useFormatUrl();
  const { provideSuiteThumbnailUrl } = useImagePath(props.urlConnector);

  const contextData = computed((): IResourcesTabsData => props.suiteData.data);

  const contextTabs = computed((): IResourcesTabsData['tabs'] =>
    contextData.value.tabs.map((tabItem) => ({
      ...tabItem,
      items: tabItem.items.map((item) => ({
        ...item,
        thumbnail: provideSuiteThumbnailUrl(item.thumbnail),
        link: provideFormatUrl(item.link),
      })),
    }))
  );

  const theme = computed((): TTheme => {
    switch (themeStyle.value) {
      case 'sustainable':
        return 'green';
      case 'connected':
        return 'cyan';
      case 'ai':
        return 'purple';
      case 'operations':
        return 'yellow';
      default:
        return 'blue';
    }
  });

  export type { IItem, IResourcesTabs, IResourcesTabsData };
</script>
